// ImportPage — main page for the web-based import tool.
// Three states based on URL and import status:
//   1. Upload (no importId) — file drop zone + paste area
//   2. Review (importId, status = pending_review) — tree + editor + action bar
//   3. Complete (importId, status = completed/failed) — execution report

import { useState, useCallback, useRef, useMemo, useEffect, type DragEvent, type ChangeEvent } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { importsApi, type ImportUploadResponse, type ImportValidationError } from '@/lib/api/imports'
import { queryKeys } from '@/lib/api/queryKeys'
import { Button } from '@/components/ui/button'
import { Upload, FileText, AlertTriangle, Loader2, ClipboardPaste, Save, ShieldCheck, Download, Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useToast } from '@/lib/hooks/use-toast'
import { ImportTree } from './ImportTree'
import { buildImportTree, buildDependencyMap, countItems, countErrors, type TreeNode } from './importTree'
import { ImportItemEditor } from './ImportItemEditor'
import { ImportReport } from './ImportReport'
import { ExecuteDialog } from './ExecuteDialog'

// --- Upload state component ---

function ImportUpload({ importId }: { importId?: string }) {
  const { slug } = useTenant()
  const navigate = useNavigate()
  const api = useApi()
  const importApi = importsApi(api)

  const [isDragOver, setIsDragOver] = useState(false)
  const [pasteMode, setPasteMode] = useState(false)
  const [pasteContent, setPasteContent] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<ImportValidationError[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const uploadMutation = useMutation({
    mutationFn: (fileOrContent: File | string) => {
      if (importId) {
        return importApi.update(importId, fileOrContent)
      }
      return importApi.upload(fileOrContent)
    },
    onSuccess: (result: ImportUploadResponse) => {
      setError(null)
      if (result.validation_errors && result.validation_errors.length > 0) {
        setValidationErrors(result.validation_errors)
      }
      navigate({ to: `/t/${slug}/import/${result.id}` })
    },
    onError: (err: Error) => {
      setValidationErrors([])
      if (err instanceof ApiRequestError) {
        setError(err.message)
      } else {
        setError(err.message || 'Upload failed')
      }
    },
  })

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file) return
    const ext = file.name.toLowerCase()
    if (!ext.endsWith('.yaml') && !ext.endsWith('.yml') && !ext.endsWith('.json')) {
      setError('Unsupported file type. Please upload a .yaml, .yml, or .json file.')
      return
    }
    if (file.size > 1024 * 1024) {
      setError('File exceeds 1MB size limit.')
      return
    }
    setError(null)
    setValidationErrors([])
    uploadMutation.mutate(file)
  }, [uploadMutation])

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    handleFiles(e.dataTransfer.files)
  }, [handleFiles])

  const handleFileInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files)
    if (e.target) e.target.value = ''
  }, [handleFiles])

  const handlePasteSubmit = useCallback(() => {
    const trimmed = pasteContent.trim()
    if (!trimmed) {
      setError('Paste content is empty.')
      return
    }
    setError(null)
    setValidationErrors([])
    uploadMutation.mutate(trimmed)
  }, [pasteContent, uploadMutation])

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">
          {importId ? 'Replace Import File' : 'Import Data'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Upload a YAML or JSON import file, or paste content directly.
          The file will be validated and you can review the contents before executing.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300/50 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">
              {validationErrors.length} validation error{validationErrors.length !== 1 ? 's' : ''} found
            </p>
            <p className="mt-0.5 text-xs text-amber-700">
              The file was accepted. You can review and fix errors before executing.
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button variant={pasteMode ? 'outline' : 'default'} size="sm" onClick={() => setPasteMode(false)}>
          <Upload className="mr-1.5 h-4 w-4" />
          Upload File
        </Button>
        <Button variant={pasteMode ? 'default' : 'outline'} size="sm" onClick={() => setPasteMode(true)}>
          <ClipboardPaste className="mr-1.5 h-4 w-4" />
          Paste Content
        </Button>
      </div>

      {!pasteMode ? (
        <div
          className={cn(
            'flex min-h-[200px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors',
            isDragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-accent/30',
            uploadMutation.isPending && 'pointer-events-none opacity-60',
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploadMutation.isPending ? (
            <>
              <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
              <p className="mt-3 text-sm font-medium text-muted-foreground">Uploading...</p>
            </>
          ) : (
            <>
              <FileText className="h-10 w-10 text-muted-foreground" />
              <p className="mt-3 text-sm font-medium">Drop your import file here, or click to browse</p>
              <p className="mt-1 text-xs text-muted-foreground">Accepts .yaml, .yml, or .json files (max 1MB)</p>
            </>
          )}
          <input ref={fileInputRef} type="file" accept=".yaml,.yml,.json" className="hidden" onChange={handleFileInput} />
        </div>
      ) : (
        <div className="space-y-3">
          <textarea
            className="min-h-[200px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="Paste your YAML or JSON import content here..."
            value={pasteContent}
            onChange={(e) => setPasteContent(e.target.value)}
            disabled={uploadMutation.isPending}
          />
          <div className="flex justify-end">
            <Button onClick={handlePasteSubmit} disabled={uploadMutation.isPending || !pasteContent.trim()}>
              {uploadMutation.isPending ? (
                <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Uploading...</>
              ) : (
                <><Upload className="mr-1.5 h-4 w-4" />Upload</>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

// --- Review state ---

function ImportReview({ importId }: { importId: string }) {
  const { slug } = useTenant()
  const api = useApi()
  const importApi = importsApi(api)
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false)

  // Server state
  const { data: imp, isLoading, error } = useQuery({
    queryKey: queryKeys.imports.detail(slug, importId),
    queryFn: () => importApi.get(importId),
  })

  // Working payload (local copy for edits)
  const [workingPayload, setWorkingPayload] = useState<Record<string, unknown> | null>(null)
  const [isDirty, setIsDirty] = useState(false)

  // Sync working payload from server when data loads or after save
  useEffect(() => {
    if (imp && !isDirty) {
      setWorkingPayload(structuredClone(imp.payload))
    }
  }, [imp, isDirty])

  // Beforeunload warning when dirty
  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  const effectivePayload = workingPayload ?? imp?.payload ?? {}

  // Build tree from effective payload + validation errors
  const tree = useMemo(
    () => buildImportTree(effectivePayload, imp?.validation_errors ?? []),
    [effectivePayload, imp?.validation_errors],
  )
  const dependencyMap = useMemo(() => buildDependencyMap(tree), [tree])

  // Find selected node
  const selectedNode = useMemo(() => {
    if (!selectedId) return null
    function find(nodes: TreeNode[]): TreeNode | null {
      for (const n of nodes) {
        if (n.id === selectedId) return n
        const found = find(n.children)
        if (found) return found
      }
      return null
    }
    return find(tree)
  }, [tree, selectedId])

  // --- Payload mutation helpers ---

  const updatePayloadItem = useCallback(
    (node: TreeNode, key: string, value: unknown) => {
      if (!workingPayload) return
      const newPayload = structuredClone(workingPayload)
      const items = getPayloadItems(newPayload, node.section)
      if (!items) return
      const item = items.find(
        (it: Record<string, unknown>) => (it.token ?? '') === node.token,
      )
      if (!item) return
      item[key] = value
      setWorkingPayload(newPayload)
      setIsDirty(true)
    },
    [workingPayload],
  )

  const removePayloadItem = useCallback(
    (node: TreeNode) => {
      if (!workingPayload) return
      const newPayload = structuredClone(workingPayload)
      const items = getPayloadItems(newPayload, node.section)
      if (!items) return
      const idx = items.findIndex(
        (it: Record<string, unknown>) => (it.token ?? '') === node.token,
      )
      if (idx === -1) return
      items.splice(idx, 1)
      setWorkingPayload(newPayload)
      setIsDirty(true)
      if (selectedId === node.id) setSelectedId(null)
    },
    [workingPayload, selectedId],
  )

  // --- Save mutation ---
  const saveMutation = useMutation({
    mutationFn: () => {
      const json = JSON.stringify(workingPayload)
      return importApi.update(importId, json)
    },
    onSuccess: () => {
      setIsDirty(false)
      queryClient.invalidateQueries({ queryKey: queryKeys.imports.detail(slug, importId) })
      toast({ title: 'Import saved', description: 'Payload updated and re-validated.' })
    },
    onError: (err: Error) => {
      toast({ title: 'Save failed', description: err.message, variant: 'destructive' })
    },
  })

  // --- Validate mutation ---
  const validateMutation = useMutation({
    mutationFn: () => importApi.validate(importId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.imports.detail(slug, importId) })
      const count = result.validation_errors?.length ?? 0
      if (count === 0) {
        toast({ title: 'Validation passed', description: 'No errors found.' })
      } else {
        toast({
          title: `${count} validation error${count !== 1 ? 's' : ''}`,
          description: 'Review and fix errors before executing.',
          variant: 'destructive',
        })
      }
    },
    onError: (err: Error) => {
      toast({ title: 'Validation failed', description: err.message, variant: 'destructive' })
    },
  })

  // --- Download ---
  const handleDownload = useCallback(
    (format: 'json' | 'yaml') => {
      const content = format === 'json'
        ? JSON.stringify(effectivePayload, null, 2)
        : jsonToSimpleYaml(effectivePayload)
      const blob = new Blob([content], { type: format === 'json' ? 'application/json' : 'application/x-yaml' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `import-${importId.substring(0, 8)}.${format === 'json' ? 'json' : 'yaml'}`
      a.click()
      URL.revokeObjectURL(url)
    },
    [effectivePayload, importId],
  )

  // --- Handlers ---

  const handleSelect = useCallback((node: TreeNode) => {
    setSelectedId(node.id)
  }, [])

  const handleRemove = useCallback(
    (node: TreeNode) => {
      const dependents = dependencyMap.get(node.token)
      if (dependents && dependents.size > 0) {
        const names = Array.from(dependents).slice(0, 5).join(', ')
        if (
          !window.confirm(
            `"${node.label}" is referenced by ${dependents.size} other item(s): ${names}${dependents.size > 5 ? '...' : ''}. Remove anyway?`,
          )
        ) {
          return
        }
      }
      removePayloadItem(node)
    },
    [dependencyMap, removePayloadItem],
  )

  const handleFieldChange = useCallback(
    (key: string, value: unknown) => {
      if (!selectedNode) return
      updatePayloadItem(selectedNode, key, value)
    },
    [selectedNode, updatePayloadItem],
  )

  const handleExecuteComplete = useCallback(() => {
    setExecuteDialogOpen(false)
    queryClient.invalidateQueries({ queryKey: queryKeys.imports.detail(slug, importId) })
  }, [queryClient, slug, importId])

  // --- Render ---

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading import...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl">
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error instanceof ApiRequestError ? error.message : 'Failed to load import'}</span>
        </div>
      </div>
    )
  }

  if (!imp) return null

  // Route to complete state if the import is finished
  if (imp.status === 'completed' || imp.status === 'failed') {
    return <ImportReport importData={imp} />
  }

  const errorCount = imp.validation_errors?.length ?? 0
  const totalItems = countItems(tree)
  const errorItems = countErrors(tree)
  const isBusy = saveMutation.isPending || validateMutation.isPending

  return (
    <div className="flex h-full flex-col">
      {/* Action bar */}
      <div className="flex items-center gap-2 border-b px-4 py-2">
        <div className="flex-1">
          <h1 className="text-lg font-semibold">Review Import</h1>
          <p className="text-xs text-muted-foreground">
            {totalItems} item{totalItems !== 1 ? 's' : ''}
            {errorCount > 0 && (
              <span className="ml-2 text-amber-700">· {errorItems} with errors</span>
            )}
            {isDirty && (
              <span className="ml-2 font-medium text-primary">· Unsaved changes</span>
            )}
          </p>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => saveMutation.mutate()}
          disabled={!isDirty || isBusy}
        >
          {saveMutation.isPending ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-1.5 h-4 w-4" />
          )}
          Save
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => validateMutation.mutate()}
          disabled={isDirty || isBusy}
          title={isDirty ? 'Save changes first' : 'Re-validate payload'}
        >
          {validateMutation.isPending ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <ShieldCheck className="mr-1.5 h-4 w-4" />
          )}
          Validate
        </Button>

        <Button variant="outline" size="sm" onClick={() => handleDownload('json')} disabled={isBusy}>
          <Download className="mr-1.5 h-4 w-4" />
          JSON
        </Button>

        <Button
          size="sm"
          onClick={() => setExecuteDialogOpen(true)}
          disabled={isDirty || errorCount > 0 || isBusy}
          title={
            isDirty
              ? 'Save changes first'
              : errorCount > 0
                ? 'Fix validation errors first'
                : 'Execute import'
          }
        >
          <Play className="mr-1.5 h-4 w-4" />
          Execute
        </Button>
      </div>

      {/* Validation error summary banner */}
      {errorCount > 0 && (
        <div className="flex items-center gap-2 border-b bg-amber-50 px-4 py-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="font-medium">
            {errorCount} validation error{errorCount !== 1 ? 's' : ''}
          </span>
          <span className="text-xs text-amber-600">— fix or acknowledge before executing</span>
        </div>
      )}

      {/* Two-panel layout */}
      <div className="flex min-h-0 flex-1">
        {/* Left panel: tree */}
        <div className="w-72 shrink-0 overflow-y-auto border-r p-2">
          <ImportTree
            tree={tree}
            dependencyMap={dependencyMap}
            selectedId={selectedId}
            onSelect={handleSelect}
            onRemove={handleRemove}
          />
        </div>

        {/* Right panel: editor */}
        <div className="flex-1 overflow-y-auto p-4">
          {selectedNode ? (
            <ImportItemEditor
              node={selectedNode}
              payload={effectivePayload}
              onChange={handleFieldChange}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Select an item from the tree to view and edit details
            </div>
          )}
        </div>
      </div>

      {/* Execute dialog */}
      <ExecuteDialog
        open={executeDialogOpen}
        importId={importId}
        onClose={() => setExecuteDialogOpen(false)}
        onComplete={handleExecuteComplete}
      />
    </div>
  )
}

// --- Payload navigation helper ---

/**
 * Get the array of items from the payload for a given section path.
 * Returns the actual array reference (for mutation).
 */
function getPayloadItems(
  payload: Record<string, unknown>,
  sectionPath: string,
): Record<string, unknown>[] | null {
  const PATHS: Record<string, string[]> = {
    'lookups': ['lookups'],
    'structure/users': ['structure', 'users'],
    'structure/ownership_roles': ['structure', 'ownership_roles'],
    'structure/registers': ['structure', 'registers'],
    'structure/control_categories': ['structure', 'control_categories'],
    'structure/event_categories': ['structure', 'event_categories'],
    'structure/action_categories': ['structure', 'action_categories'],
    'structure/framework_categories': ['structure', 'framework_categories'],
    'structure/event_types': ['structure', 'event_types'],
    'permissions/roles': ['permissions', 'roles'],
    'permissions/grants': ['permissions', 'grants'],
    'permissions/assignments': ['permissions', 'assignments'],
    'objects/risks': ['objects', 'risks'],
    'objects/controls': ['objects', 'controls'],
    'objects/events': ['objects', 'events'],
    'objects/actions': ['objects', 'actions'],
    'appetite': ['appetite'],
    'assessments': ['assessments'],
    'updates': ['updates'],
    'obligations': ['obligations'],
  }

  const keys = PATHS[sectionPath]
  if (!keys) return null

  let current: unknown = payload
  for (const key of keys) {
    if (current == null || typeof current !== 'object') return null
    current = (current as Record<string, unknown>)[key]
  }

  return Array.isArray(current) ? (current as Record<string, unknown>[]) : null
}

/**
 * Simple JSON-to-YAML-like formatter for download.
 * Not a real YAML serializer — just produces readable output for simple structures.
 */
function jsonToSimpleYaml(obj: unknown, indent = 0): string {
  const pad = '  '.repeat(indent)
  if (obj === null || obj === undefined) return 'null'
  if (typeof obj === 'string') return obj.includes('\n') ? `|\n${pad}  ${obj.replace(/\n/g, `\n${pad}  `)}` : obj
  if (typeof obj === 'number' || typeof obj === 'boolean') return String(obj)
  if (Array.isArray(obj)) {
    if (obj.length === 0) return '[]'
    return obj.map((item) => {
      if (typeof item === 'object' && item !== null) {
        const inner = jsonToSimpleYaml(item, indent + 1)
        return `${pad}- ${inner.trimStart()}`
      }
      return `${pad}- ${jsonToSimpleYaml(item, indent + 1)}`
    }).join('\n')
  }
  if (typeof obj === 'object') {
    const entries = Object.entries(obj as Record<string, unknown>).filter(([, v]) => v !== null && v !== undefined)
    if (entries.length === 0) return '{}'
    return entries.map(([key, val]) => {
      if (typeof val === 'object' && val !== null) {
        return `${pad}${key}:\n${jsonToSimpleYaml(val, indent + 1)}`
      }
      return `${pad}${key}: ${jsonToSimpleYaml(val, indent + 1)}`
    }).join('\n')
  }
  return String(obj)
}

// --- Main page component ---

export function ImportPage({ importId }: { importId?: string }) {
  if (!importId) {
    return (
      <div className="p-6">
        <ImportUpload />
      </div>
    )
  }

  return (
    <div className="h-full">
      <ImportReview importId={importId} />
    </div>
  )
}
